import './AnnouncementPopup.scss';
import React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

export interface AnnouncementPopupProps {
  visible: boolean;
  /** Close (X) and "Maybe later" — the caller persists the "seen" entry here. */
  onDismiss: () => void;
  onCta: () => void;
  /** Badge text; the shell adds the sparkle icon in front of it. */
  badge: React.ReactNode;
  title: React.ReactNode;
  description: string;
  laterLabel: string;
  /** CTA content — text, optionally with an icon (the Ads look appends `ico--right`). */
  ctaLabel: React.ReactNode;
  /**
   * Classes on the dialog root: the popup's own block class (for the styles of its `children`) and,
   * optionally, a shell look modifier — `announcement-popup--panel` for the gradient-header look of the
   * Insights mock; without it the hairline look of the Ads mock applies.
   */
  className: string;
  /** Body content between the header and the footer (illustration, feature list, ...). */
  children?: React.ReactNode;
}

// The "new feature" announcement shell shared by AdsAnnouncementPopup and InsightsAnnouncementPopup:
// PrimeReact Dialog without its header, own close button, header (badge + title + description), the
// caller's body and the "Maybe later" / CTA footer. WHEN it opens and the per-user "seen" entry live in
// the callers (see useAnnouncement); the two visual looks are SCSS modifiers, not props.
export const AnnouncementPopup: React.FC<AnnouncementPopupProps> = ({
  visible,
  onDismiss,
  onCta,
  badge,
  title,
  description,
  laterLabel,
  ctaLabel,
  className,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      visible={visible}
      onHide={onDismiss}
      showHeader={false}
      draggable={false}
      resizable={false}
      className={`dialog-box dialog-box--no-footer announcement-popup ${className}`}
    >
      <Button
        type="button"
        className="announcement-popup__close"
        onClick={onDismiss}
        aria-label={t('close')}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </Button>

      <div className="announcement-popup__header">
        <span className="announcement-popup__badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" />
          </svg>
          {badge}
        </span>
        <h2 className="announcement-popup__title">{title}</h2>
        <p className="announcement-popup__desc">{description}</p>
      </div>

      {children}

      <div className="announcement-popup__footer">
        <Button
          type="button"
          className="announcement-popup__later"
          label={laterLabel}
          onClick={onDismiss}
        />
        <Button type="button" className="announcement-popup__cta" onClick={onCta}>
          {ctaLabel}
        </Button>
      </div>
    </Dialog>
  );
};

export default AnnouncementPopup;
